import React, { useState, useEffect, useRef, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { fetchCompany } from "../utils/plan";

// ============================================================
// お支払い・プラン（2026-10-09 に fincode から Stripe へ切り替え）
//
// ・カード番号はこのアプリでは一切扱わない。入力は Stripe の支払い画面（Checkout）で行う。
// ・解約・カード変更・プラン変更は Stripe の管理画面（Customer Portal）で行う。
// ・有料かどうかの判定は src/utils/plan.js（billingStatus === "active" またはテスター）。
//   billingStatus を書き換えるのはサーバー（functions/index.js の Stripe の関数）だけ。
// ============================================================

// 画面に出すプラン。id は Firestore の companies.plan と同じ値（functions/stripe.js の PLAN_LOOKUP_KEYS と対応）
const PLANS = [
  { id: "small",    name: "Smallプラン",    price: "月額500円（税込）",   limit: "社員20名まで" },
  { id: "standard", name: "Standardプラン", price: "月額1,500円（税込）", limit: "社員50名まで" },
];
const PLAN_NAME = { trial: "無料プラン", free: "無料プラン", small: "Smallプラン", standard: "Standardプラン" };

const STATUS_LABEL = {
  unregistered: { text: "無料プラン",   color: "#2471a3" },
  trial:        { text: "無料プラン",   color: "#2471a3" },
  free:         { text: "無料プラン",   color: "#2471a3" },
  active:       { text: "有料プラン",   color: "#1e8449" },
  past_due:     { text: "支払い失敗",   color: "#c0392b" },
  canceled:     { text: "解約済み（無料プラン）", color: "#626567" },
};

/** Stripe から戻ってきたときの URL（?billing=success&session_id=… など）を読む */
function readReturnParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    const kind = p.get("billing");
    if (!kind) return null;
    return { kind, sessionId: p.get("session_id") || "" };
  } catch {
    return null;
  }
}

/** 戻りの印を URL から消す（再読み込みで同じ確認を繰り返さないように） */
function clearReturnParams() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("billing");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
  } catch { /* 消せなくても動作には影響しない */ }
}

const fmtDate = (ts) => {
  const d = ts?.toDate?.();
  return d ? d.toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" }) : "";
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function BillingSettings({ companyId }) {
  const [company, setCompany]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(PLANS[0].id);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  // Stripe から戻ったときのお知らせ。kind: checking | done | info | slow
  const [notice, setNotice]     = useState(null);
  const [returnInfo]            = useState(readReturnParams);
  const startedRef  = useRef(false);
  const noticeRef   = useRef(null);
  const functions = getFunctions(undefined, "us-central1");

  const loadCompany = useCallback(async () => {
    if (!companyId) { setLoading(false); return null; }
    try {
      const c = await fetchCompany(companyId);
      setCompany(c);
      return c;
    } catch (e) {
      console.error("会社情報の取得に失敗しました:", e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadCompany(); }, [loadCompany]);

  // お知らせが出たら、読み上げソフトがすぐ読めるようにそこへ移る
  useEffect(() => {
    if (notice && noticeRef.current) noticeRef.current.focus();
  }, [notice]);

  // Stripe から戻ってきたときの処理（確認中 → 反映済み）
  useEffect(() => {
    if (!returnInfo || startedRef.current) return;
    startedRef.current = true;
    clearReturnParams();

    if (returnInfo.kind === "cancel") {
      setNotice({ kind: "info", text: "お申し込みを取りやめました。料金はかかっていません。" });
      return;
    }
    if (returnInfo.kind === "portal") {
      setNotice({ kind: "info", text: "Stripe の管理画面から戻りました。最新の状態を表示しています。変更の反映には数秒かかることがあります。" });
      (async () => { for (let i = 0; i < 3; i++) { await sleep(3000); await loadCompany(); } })();
      return;
    }
    if (returnInfo.kind !== "success") return;

    setNotice({ kind: "checking", text: "お支払いを確認しています。この画面のまま、しばらくお待ちください。" });
    (async () => {
      // まず Stripe へ直接たずねる（Webhook が遅れても進めるように）。失敗しても、下の見回りで拾う
      if (returnInfo.sessionId) {
        try {
          await httpsCallable(functions, "confirmCheckoutSession")({ sessionId: returnInfo.sessionId });
        } catch (e) {
          console.warn("お支払いの確認（直接）に失敗しました。反映を待ちます:", e);
        }
      }
      for (let i = 0; i < 20; i++) {
        const c = await loadCompany();
        if (c?.billingStatus === "active" && c?.billingProvider === "stripe") {
          const name = PLAN_NAME[c.plan] || "有料プラン";
          setNotice({ kind: "done", text: `反映済みです。${name}が有効になりました。ご利用ありがとうございます。` });
          return;
        }
        await sleep(3000);
      }
      setNotice({
        kind: "slow",
        text: "お支払いは Stripe で受け付けましたが、この画面への反映に時間がかかっています。数分たってから、この画面を開き直してください。反映されない場合はお問い合わせください。",
      });
    })();
    // returnInfo は最初の1回だけ見る
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goCheckout = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await httpsCallable(functions, "createCheckoutSession")({
        plan: selectedPlan,
        origin: window.location.origin,
      });
      if (!res.data?.url) throw new Error("お支払い画面のURLを受け取れませんでした");
      window.location.assign(res.data.url);   // Stripe の支払い画面へ移る
    } catch (err) {
      console.error(err);
      setError(err.message || "お支払い画面を開けませんでした。少し時間をおいてお試しください。");
      setBusy(false);
    }
  };

  const goPortal = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await httpsCallable(functions, "createPortalSession")({ origin: window.location.origin });
      if (!res.data?.url) throw new Error("管理画面のURLを受け取れませんでした");
      window.location.assign(res.data.url);   // Stripe の管理画面へ移る
    } catch (err) {
      console.error(err);
      setError(err.message || "お支払いの管理画面を開けませんでした。少し時間をおいてお試しください。");
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={st.container}><p role="status" style={{ color: "#555" }}>読み込み中です…</p></div>;
  }

  const status = company?.billingStatus || "unregistered";
  const statusInfo = STATUS_LABEL[status] || STATUS_LABEL.unregistered;
  const viaStripe = company?.billingProvider === "stripe";
  const isTester = company?.isTester === true;
  const planName = PLAN_NAME[company?.plan] || "";
  const canApply = status !== "active" && status !== "past_due";
  const checking = notice?.kind === "checking";

  const COMPARE = [
    { feature: "社員数",       free: "5名まで",  small: "20名まで", standard: "50名まで" },
    { feature: "出退勤打刻",   free: "○",        small: "○",        standard: "○" },
    { feature: "お知らせ",     free: "○",        small: "○",        standard: "○" },
    { feature: "書類提出",     free: "○",        small: "○",        standard: "○" },
    { feature: "ライブラリー", free: "×",        small: "○",        standard: "○" },
    { feature: "給与明細の閲覧", free: "○",      small: "○",        standard: "○" },
    { feature: "給与計算",     free: "×",        small: "○",        standard: "○" },
    { feature: "CSV出力",      free: "○",        small: "○",        standard: "○" },
    { feature: "サポート",     free: "×",        small: "メール",   standard: "優先メール" },
  ];
  // 読み上げで「○」「×」が記号として読まれないよう、言葉を添える
  const cellLabel = (v) => (v === "○" ? "使える" : v === "×" ? "使えない" : v);

  const noticeColor = { checking: "#2471a3", done: "#1e8449", info: "#2471a3", slow: "#b9770e" };

  return (
    <div style={st.container}>
      <h2 style={st.title}>お支払い・プラン</h2>

      {/* Stripe から戻ったときのお知らせ（読み上げソフトに伝わるよう role="status"） */}
      <div role="status" aria-live="polite">
        {notice && (
          <div ref={noticeRef} tabIndex={-1}
            style={{ ...st.card, borderLeft: `6px solid ${noticeColor[notice.kind] || "#2471a3"}`, outline: "none" }}>
            <p style={{ margin: 0, fontWeight: "bold", color: noticeColor[notice.kind] || "#2471a3" }}>
              {notice.kind === "checking" ? "確認中：" : notice.kind === "done" ? "完了：" : "お知らせ："}
              {notice.text}
            </p>
          </div>
        )}
      </div>

      {/* 現在の状態 */}
      <section style={st.card} aria-labelledby="billing-now">
        <h3 id="billing-now" style={st.subTitle}>現在のご契約</h3>
        <dl style={{ margin: 0 }}>
          <div style={st.row}>
            <dt style={st.label}>利用状況</dt>
            <dd style={{ margin: 0 }}><span style={{ ...st.badge, backgroundColor: statusInfo.color }}>{statusInfo.text}</span></dd>
          </div>
          {status === "active" && planName && (
            <div style={st.row}>
              <dt style={st.label}>プラン</dt>
              <dd style={{ ...st.value, margin: 0 }}>{planName}</dd>
            </div>
          )}
          {viaStripe && status === "active" && company?.billingPeriodEnd && (
            <div style={st.row}>
              <dt style={st.label}>{company.billingCancelAtPeriodEnd ? "ご利用の最終日" : "次回のお支払い日"}</dt>
              <dd style={{ ...st.value, margin: 0 }}>{fmtDate(company.billingPeriodEnd)}</dd>
            </div>
          )}
          {isTester && (
            <div style={st.row}>
              <dt style={st.label}>テスター</dt>
              <dd style={{ ...st.value, margin: 0 }}>運営の指定により、お支払いなしで有料機能をお使いいただけます</dd>
            </div>
          )}
        </dl>
        {viaStripe && status === "active" && company?.billingCancelAtPeriodEnd && (
          <p style={{ color: "#b9770e", marginTop: "12px", marginBottom: 0 }}>
            解約のお手続き済みです。上の日付までは有料プランをお使いいただけ、その後は無料プランに戻ります。
          </p>
        )}
      </section>

      {/* 支払い失敗 */}
      {status === "past_due" && (
        <section role="alert" style={{ ...st.card, borderLeft: "6px solid #c0392b" }}>
          <p style={{ color: "#c0392b", fontWeight: "bold", marginTop: 0 }}>直近のお支払いができませんでした</p>
          <p style={{ color: "#333", fontSize: "15px" }}>
            カードの有効期限や利用枠をご確認のうえ、カード情報を更新してください。
            更新されるまで、有料プランの機能（給与計算・ライブラリー）は使えません。
          </p>
          {viaStripe
            ? <button type="button" style={st.primaryBtn} onClick={goPortal} disabled={busy}>
                {busy ? "Stripe の画面を開いています…" : "カード情報を更新する（Stripe の画面を開く）"}
              </button>
            : <p style={{ color: "#333" }}>アプリ内の「お問い合わせ」からご連絡ください。</p>}
        </section>
      )}

      {/* 有料プラン契約中 */}
      {status === "active" && (
        <section style={st.card} aria-labelledby="billing-manage">
          <h3 id="billing-manage" style={st.subTitle}>プランの変更・カードの変更・解約</h3>
          {viaStripe ? (
            <>
              <p style={st.text}>
                次のボタンで、決済会社 Stripe のお客様用画面が開きます。
                そこで、プランの変更、カードの変更、請求書の確認、解約ができます。
                終わったら、画面の「戻る」からこのアプリに戻れます。
              </p>
              <button type="button" style={st.primaryBtn} onClick={goPortal} disabled={busy}>
                {busy ? "Stripe の画面を開いています…" : "お支払いの管理画面を開く（Stripe）"}
              </button>
            </>
          ) : (
            <p style={st.text}>
              運営が設定した有料プランです。プランの変更や解約は、アプリ内の「お問い合わせ」からご連絡ください。
            </p>
          )}
        </section>
      )}

      {/* 申し込み（無料・解約済み） */}
      {canApply && (
        <section style={st.card} aria-labelledby="billing-apply">
          <h3 id="billing-apply" style={st.subTitle}>有料プランのお申し込み</h3>
          <fieldset style={st.fieldset} disabled={busy || checking}>
            <legend style={st.legend}>お申し込みになるプランを選んでください</legend>
            {PLANS.map((p) => {
              const on = selectedPlan === p.id;
              return (
                <label key={p.id} style={{ ...st.planCard, border: on ? "3px solid #1a5276" : "2px solid #bbb", backgroundColor: on ? "#eaf2f8" : "#fff" }}>
                  <input type="radio" name="billing-plan" value={p.id} checked={on}
                    onChange={() => setSelectedPlan(p.id)} style={{ width: "20px", height: "20px", marginRight: "10px" }} />
                  <span>
                    <span style={{ fontWeight: "bold", color: "#1a2e40", fontSize: "17px" }}>{p.name}</span>
                    <span style={{ display: "block", color: "#1e8449", fontWeight: "bold", fontSize: "17px" }}>{p.price}</span>
                    <span style={{ display: "block", color: "#333", fontSize: "14px" }}>{p.limit}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>
          <p id="billing-checkout-help" style={st.text}>
            次のボタンを押すと、決済会社 Stripe のお支払い画面に移ります。そこでカード番号を入力してください。
            カード番号はこのアプリには保存されません。お申し込みが済むと、自動でこの画面に戻ります。
            毎月同じ日に自動でお支払いになります。解約はいつでもこの画面から行えます。
          </p>
          <button type="button" style={st.primaryBtn} onClick={goCheckout} disabled={busy || checking}
            aria-describedby="billing-checkout-help">
            {busy ? "Stripe のお支払い画面を開いています…" : `${PLANS.find((p) => p.id === selectedPlan)?.name}で申し込む（Stripe のお支払い画面へ進む）`}
          </button>
        </section>
      )}

      {error && <p role="alert" style={st.errorMsg}>{error}</p>}

      {/* プラン比較表 */}
      <section style={st.card} aria-labelledby="billing-compare">
        <h3 id="billing-compare" style={st.subTitle}>プランの比較</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <caption style={st.srOnly}>無料プラン・Smallプラン・Standardプランで使える機能の比較</caption>
            <thead>
              <tr>
                <th scope="col" style={st.th}>機能</th>
                <th scope="col" style={{ ...st.th, ...st.thPlan }}>無料プラン<br/><span style={st.thPrice}>0円</span></th>
                <th scope="col" style={{ ...st.th, ...st.thPlan }}>Smallプラン<br/><span style={st.thPrice}>月額500円</span></th>
                <th scope="col" style={{ ...st.th, ...st.thPlan, backgroundColor: "#1a2e40", color: "#fff" }}>Standardプラン<br/><span style={{ ...st.thPrice, color: "#d6eaf8" }}>月額1,500円</span></th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#f8fafc" : "#fff" }}>
                  <th scope="row" style={{ ...st.td, textAlign: "left", fontWeight: "normal" }}>{row.feature}</th>
                  {[row.free, row.small, row.standard].map((v, j) => (
                    <td key={j} style={{ ...st.td, textAlign: "center", color: v === "×" ? "#888" : "#1a2e40" }}>
                      <span aria-hidden="true">{v}</span>
                      <span style={st.srOnly}>{cellLabel(v)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p style={{ fontSize: "13px", color: "#555" }}>
        お支払いは決済会社 Stripe（ストライプ）を通じて行われます。カード情報は Stripe が管理し、当サービスには保存されません。
      </p>
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "720px", margin: "0 auto" },
  title: { color: "#1a2e40", marginBottom: "24px" },
  subTitle: { color: "#1a2e40", marginTop: 0, marginBottom: "16px" },
  text: { color: "#333", fontSize: "15px", lineHeight: 1.7 },
  card: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px", flexWrap: "wrap" },
  label: { color: "#444", fontSize: "15px" },
  value: { color: "#1a2e40", fontWeight: "bold" },
  badge: { color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" },
  primaryBtn: { padding: "14px 24px", backgroundColor: "#1a2e40", color: "#fff", border: "2px solid #1a2e40", borderRadius: "8px", cursor: "pointer", fontSize: "16px", lineHeight: 1.5 },
  fieldset: { border: "none", padding: 0, margin: "0 0 16px", display: "flex", gap: "12px", flexWrap: "wrap" },
  legend: { fontWeight: "bold", color: "#1a2e40", marginBottom: "12px", fontSize: "15px" },
  planCard: { flex: 1, minWidth: "200px", padding: "16px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "flex-start" },
  errorMsg: { color: "#c0392b", fontSize: "15px", fontWeight: "bold", marginBottom: "12px" },
  th: { padding: "10px 12px", borderBottom: "2px solid #ddd", textAlign: "center", color: "#1a2e40", whiteSpace: "nowrap" },
  thPlan: { backgroundColor: "#eef3f8", borderRadius: "4px" },
  thPrice: { fontSize: "13px", fontWeight: "normal", color: "#1e8449" },
  td: { padding: "10px 12px", borderBottom: "1px solid #eef3f8", color: "#1a2e40", whiteSpace: "nowrap" },
  srOnly: { position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 },
};

export default BillingSettings;
